import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isEqual } from 'lodash/fp';

import { DEFAULT_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { TableState } from 'dashboard/lib/interfaces/TableState';
import { Environment } from 'dashboard/models/environment';
import { SAR, SARStatus } from 'dashboard/models/sar';
import { PaginationParams } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

export interface SARsFilterParams {
  omni?: string;
  status?: SARStatus[];
  createdAtStart?: Iso8601DateString;
  createdAtEnd?: Iso8601DateString;
}

export type SARs = Record<string, SAR[]>;

export interface SARsState extends TableState<SARsFilterParams> {
  sars: SARs;
}

export const initialSARsFilterState: SARsFilterParams = {
  createdAtStart: undefined,
  createdAtEnd: undefined,
  status: Object.values(SARStatus).filter((status) => status !== SARStatus.Archived),
  omni: undefined,
};

export const initialState: SARsState = {
  sars: {},
  paginationParams: undefined,
  pageSize: DEFAULT_PAGE_SIZE,
  filters: initialSARsFilterState,
  sortParams: {
    sort: 'created_at',
    shouldSortDescending: true,
  },
  currentPage: 1,
};

const { reducer, actions } = createSlice({
  name: 'sars',
  initialState,
  reducers: {
    setSARs: (
      state,
      action: PayloadAction<{
        sars: SAR[];
        environment: Environment;
        paginationParams: PaginationParams;
      }>,
    ) => {
      state.sars[action.payload.environment.id] = action.payload.sars;
      state.paginationParams = action.payload.paginationParams;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setFilters: (state, action: PayloadAction<SARsFilterParams>) => {
      const newFilters = {
        ...state.filters,
        ...action.payload,
      };

      return isEqual(newFilters, state.filters) ? state : { ...state, filters: newFilters };
    },
    setSortParams: (state, action: PayloadAction<SortParams>) => {
      return {
        ...state,
        sortParams: {
          ...action.payload,
        },
      };
    },
  },
});

export const { setSARs, setCurrentPage, setPageSize, setFilters, setSortParams } = actions;
export const SARsReducer = reducer;
